"use client";

import Image from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ReactLenis } from 'lenis/react'
import { SectionFooter } from "../Main/SectionFooter";
import gsap from "gsap";
import { TextPlugin } from 'gsap/TextPlugin';
import { SplitText } from "gsap/all";
import { ScrollTrigger } from "gsap/all";

gsap.registerPlugin(SplitText, ScrollTrigger, TextPlugin);

export const AboutPageSection = () => {
    // ANIMATIONS

    const titleRef = useRef()
    const titleRef2 = useRef()
    const descriptionRef = useRef()
    const lineRef = useRef()
    const itemRefs = useRef([]);

    const teamMembers = [
        { name: "Allocious Franklin R", title: "CEO and Founder" },
        { name: "Akash N", title: "CQO and Co-Founder" },
        { name: "AkashMadhav Y", title: "CTO & Co-Founder" },
        { name: "Mohamed Al Silmi", title: "CFO & Co-Founder" },
    ];

    useEffect(() => {

        // title animation
        const titleSplit = new SplitText(titleRef.current, { type: "words, chars" });
        gsap.fromTo(titleSplit.chars, { 'will-change': 'opacity, transform', filter: 'blur(8px)', opacity: 0, yPercent: 50 }, { delay: 0.2, opacity: 1, filter: 'blur(0px)', yPercent: 0, stagger: 0.02, duration: 0.75, ease: "power1" });

        // description animation
        gsap.to(descriptionRef.current, { opacity: 1, filter: 'blur(0px)', duration: 1, delay: 0.6 })

        // line animation
        gsap.fromTo(lineRef.current, { opacity: 0, filter: 'blur(8px)' }, { opacity: 1, filter: 'blur(0px)', duration: 0.5, delay: 0.5 })

        // title 2 animation
        const titleSplit2 = new SplitText(titleRef2.current, { type: "words" });
        gsap.fromTo(titleSplit2.words, { 'will-change': 'opacity', filter: 'blur(8px)', opacity: 0 }, { opacity: 1, filter: 'blur(0px)', stagger: 0.025, ease: 'sine', scrollTrigger: { trigger: titleRef2.current, start: "top 95%", end: "bottom center", scrub: true } });

        // team member boxes animations
        itemRefs.current.forEach((item, index) => {
            gsap.fromTo(item,
                { yPercent: 100, opacity: 0, filter: "blur(8px)" },
                {
                    yPercent: 0,
                    opacity: 1,
                    filter: "blur(0px)",
                    duration: 0.75,
                    delay: index * 0.2,
                    ease: "power3",
                    scrollTrigger: {
                        trigger: ".about-team",
                        start: "top bottom"
                    }
                }
            );
        });

    }, [])

    // STICKY SECTION

    const item1Ref = useRef(null);
    const item2Ref = useRef(null);
    const item3Ref = useRef(null);
    const item4Ref = useRef(null);

    useEffect(() => {
        const refs = [item1Ref, item2Ref, item3Ref, item4Ref];

        refs.forEach((ref, position) => {
            const el = ref.current;
            const isLast = position === refs.length - 1;

            gsap.set(el, { willChange: "transform, filter" });

            const timeline = gsap.timeline({
                scrollTrigger: {
                    trigger: el,
                    start: 'center center',
                    end: '350%',
                    scrub: true,
                },
            });

            timeline
                .to(el, {
                    ease: 'none',
                    startAt: { filter: 'blur(0px)' },
                    filter: isLast ? 'blur(0px)' : 'blur(3px)',
                    scrollTrigger: {
                        trigger: el,
                        start: 'center center',
                        end: '+=100%',
                        scrub: true,
                    },
                }, 0)
                .to(el, {
                    ease: 'none',
                    scale: isLast ? 1 : 0.55,
                    yPercent: isLast ? 0 : -45,
                }, 0);
        });
    }, []);

    return (
        <ReactLenis root>
            <section className="about">
                <div className="about-content">
                    <div className="about-content-top">
                        <div className="about-content-textbox">
                            <div className="titlebox">
                                <div className="titlebox-gradient" />
                                <h1 className="headline white" ref={titleRef} >A Team Built for Delivery</h1>
                            </div>
                            <p className="description grey opacity-blur" ref={descriptionRef} >We&apos;re engineers, designers, and problem-solvers who build digital systems that drive real business outcomes — from concept to deployment.</p>
                        </div>
                        <div className="about-divider" ref={lineRef} />
                    </div>
                    <div className="about-team">
                        <div className="about-team-container">
                            {teamMembers.map((member, index) => (
                                <div className="about-team-item" key={index} ref={el => itemRefs.current[index] = el} >
                                    <p className="description white" >{member.name}</p>
                                    <p className="description white" >{member.title}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="about-divider" />
                    <div className="about-whyus" >
                        <p className="description about-whyus-description grey" >WHY US</p>
                        <p className="subheadline about-whyus-subheadline white" ref={titleRef2} >Built for Clarity. Engineered for Scale.<br /><br />
                            We approach every engagement as infrastructure.<br /><br />
                            Websites convert.<br />
                            Automations remove friction.<br />
                            Software supports real operations.<br /><br />
                            We design systems that remain reliable as your business grows.<br /><br />
                            Scale demands stability — not noise.</p>
                    </div>
                    <div className="about-divider" />
                    <div className="about-sticky-container">
                        <div className="about-sticky-item" ref={item1Ref} >
                            <div className="about-sticky-item-left">
                                <div className="about-sticky-item-left-textbox">
                                    <h2 className="headline white" >High-Performance Websites That Build Trust</h2>
                                    <div className="description about-sticky-item-left-textbox-description grey">
                                        <p><strong className="white">What it really is:</strong><br />Websites engineered for credibility and conversion — combining clean design, fast performance, and strategic structure to signal authority from the first impression.</p>
                                        <br />
                                        <p><strong className="white">Why it matters:</strong><br />You don&apos;t lose deals in meetings — you lose them when prospects visit a weak website.</p>
                                    </div>
                                </div>

                            </div>
                            <div className="about-sticky-item-right">
                                <div className="about-sticky-item-right-imagebox">
                                    <Image priority src="/images/mockup4.webp" className="about-sticky-item-right-image" alt="High-performance website design by QuroixLabs"  width={1920} height={1080} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 50vw" />
                                </div>
                            </div>
                        </div>
                        <div className="about-sticky-item" ref={item2Ref} >
                            <div className="about-sticky-item-left">
                                <div className="about-sticky-item-left-textbox">
                                    <h2 className="headline white" >Intelligent Automation That Scales Operations</h2>
                                    <div className="description about-sticky-item-left-textbox-description grey">
                                        <p><strong className="white">What it really is:</strong><br />Custom workflows and automation systems that eliminate manual work across sales, operations, and internal processes — turning repetitive tasks into reliable systems.</p>
                                        <br />
                                        <p><strong className="white">Why it matters:</strong><br />Growth without automation doesn&apos;t scale. It creates bottlenecks.</p>
                                    </div>
                                </div>

                            </div>
                            <div className="about-sticky-item-right">
                                <div className="about-sticky-item-right-imagebox">
                                    <Image priority src="/images/mockup12.webp" className="about-sticky-item-right-image" alt="Intelligent automation system by QuroixLabs"  width={1920} height={1080} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 50vw" />
                                </div>
                            </div>
                        </div>
                        <div className="about-sticky-item" ref={item3Ref} >
                            <div className="about-sticky-item-left">
                                <div className="about-sticky-item-left-textbox">
                                    <h2 className="headline white" >Custom Software Built for Your Operations</h2>
                                    <div className="description about-sticky-item-left-textbox-description grey">
                                        <p><strong className="white">What it really is:</strong><br />Tailored internal tools and platforms designed around how your business actually operates — not generic software that forces you to adapt your workflow.</p>
                                        <br />
                                        <p><strong className="white">Why it matters:</strong><br />When your software matches your processes, efficiency becomes a competitive advantage.</p>
                                    </div>
                                </div>

                            </div>
                            <div className="about-sticky-item-right">
                                <div className="about-sticky-item-right-imagebox">
                                    <Image priority src="/images/mockup7.webp" className="about-sticky-item-right-image" alt="Custom software development by QuroixLabs"  width={1920} height={1080} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 50vw" />
                                </div>
                            </div>
                        </div>
                        <div className="about-sticky-item" ref={item4Ref} >
                            <div className="about-sticky-item-left">
                                <div className="about-sticky-item-left-textbox">
                                    <h2 className="headline white" >Integrated Systems That Work Together</h2>
                                    <div className="description about-sticky-item-left-textbox-description grey">
                                        <p><strong className="white">What it really is:</strong><br />The integration of websites, automations, custom software, and third-party tools into a unified digital ecosystem that works seamlessly.</p>
                                        <br />
                                        <p><strong className="white">Why it matters:</strong><br />Disconnected systems create inefficiency — in time, data accuracy, and decision-making speed.</p>
                                    </div>
                                </div>

                            </div>
                            <div className="about-sticky-item-right">
                                <div className="about-sticky-item-right-imagebox">
                                    <Image priority src="/images/mockup11.webp" className="about-sticky-item-right-image" alt="Integrated digital systems by QuroixLabs"  width={1920} height={1080} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 75vw, 50vw" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <SectionFooter />
        </ReactLenis>
    );
};